import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "327",
  "slug": "largest-lakes-in-the-world-ranked",
  "title": "The 10 Largest Lakes in the World, Ranked (And Why the Caspian Is Really a Lake)",
  "excerpt": "From an inland sea bigger than Germany to the Great Lakes of North America, here are the 10 largest lakes on Earth by surface area — plus the surprisingly tricky question of what counts as a lake.",
  "date": "2026-06-02",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1617803676663-3aa36f6d148c?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxhZXJpYWwlMjBsYWtlJTIwbGFuZHNjYXBlfGVufDF8MHx8fDE3ODAzOTMyNzN8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "content": [
    {
      "type": "paragraph",
      "text": "Ranking the world's largest lakes sounds straightforward until you hit the first entry and realise it's called a sea. Lakes are trickier to define than rivers or mountains, and the very biggest one on Earth has spent decades at the centre of a legal and geographic argument. Here's the top ten by surface area — and the reasoning behind the order."
    },
    {
      "type": "heading",
      "text": "What Counts as a Lake?"
    },
    {
      "type": "paragraph",
      "text": "A lake is, loosely, a sizeable body of water surrounded by land and not part of the ocean. The grey areas come from bodies that are very large, very salty, or connected to the sea by a narrow strait. By convention, geographers usually rank lakes by surface area, and that's the measure used here — though volume and depth tell very different stories, as you'll see."
    },
    {
      "type": "heading",
      "text": "The 10 Largest Lakes by Surface Area"
    },
    {
      "type": "list",
      "items": [
        "1. Caspian Sea — about 371,000 km². The largest enclosed body of water on Earth, bordered by Russia, Kazakhstan, Turkmenistan, Iran, and Azerbaijan. Despite the name, it's a lake.",
        "2. Lake Superior — about 82,100 km². The largest freshwater lake in the world by surface area, on the US–Canada border.",
        "3. Lake Victoria — about 59,950 km². Africa's largest lake and the largest tropical lake on Earth, shared by Tanzania, Uganda, and Kenya.",
        "4. Lake Huron — about 59,600 km². One of North America's Great Lakes.",
        "5. Lake Michigan — about 58,000 km². The only Great Lake entirely within the United States.",
        "6. Lake Tanganyika — about 32,900 km². The world's second-deepest and second-largest lake by volume, in East Africa.",
        "7. Lake Baikal — about 31,500 km². The deepest lake on Earth and the largest by volume, in Siberia.",
        "8. Great Bear Lake — about 31,000 km². The largest lake entirely within Canada.",
        "9. Lake Malawi (Lake Nyasa) — about 29,500 km². A long, deep rift lake in southeastern Africa.",
        "10. Great Slave Lake — about 27,200 km². The deepest lake in North America, in Canada's Northwest Territories."
      ]
    },
    {
      "type": "heading",
      "text": "How These Lakes Formed"
    },
    {
      "type": "paragraph",
      "text": "The list isn't a random scatter — most of these giants owe their size to one of two forces. Several are rift lakes, formed where the Earth's crust is slowly pulling apart: Tanganyika, Baikal, and Malawi all sit in long, deep tectonic rifts, which is exactly why they're so narrow, so deep, and so old. Others are glacial: Superior, Huron, Michigan, Great Bear, and Great Slave were all carved or dammed by the immense ice sheets that ground across North America during the last Ice Age and left water-filled basins behind. The Caspian is a different story again — a leftover of an ancient sea that once connected to the global ocean and was gradually cut off."
    },
    {
      "type": "heading",
      "text": "The Caspian: Sea by Name, Lake by Nature"
    },
    {
      "type": "paragraph",
      "text": "The Caspian Sea is enormous — bigger in area than Germany — and it has saltwater, which is why early geographers called it a sea. But it's landlocked, with no natural outflow to the ocean, which fits the definition of a lake. The label isn't just trivia: for years, the five bordering countries argued over whether the Caspian was a 'sea' or a 'lake' precisely because the answer changed how its oil, gas, and fishing rights would be divided under international law. A 2018 convention settled the politics with a special legal status, but geographically it remains the world's largest lake."
    },
    {
      "type": "heading",
      "text": "The Great Lakes: Almost One Lake"
    },
    {
      "type": "paragraph",
      "text": "Three of the top ten — Superior, Huron, and Michigan — are North American Great Lakes, and there's a wrinkle worth knowing. Lake Michigan and Lake Huron sit at the same elevation and are joined by the wide Straits of Mackinac, so hydrologically they're a single body of water sometimes called Lake Michigan–Huron. Counted that way, the combined lake would leap up the rankings, second only to the Caspian. By tradition, though, they're named and ranked separately."
    },
    {
      "type": "heading",
      "text": "Baikal: Not the Widest, But the Deepest"
    },
    {
      "type": "paragraph",
      "text": "Ranking by surface area undersells Lake Baikal badly. It's only seventh by area, but it plunges to over 1,600 metres deep, making it the deepest lake on the planet — and it holds more water than all five North American Great Lakes combined, roughly a fifth of the world's unfrozen freshwater. Surface area is just one way to be 'biggest', and Baikal is the reminder that depth and volume can rewrite the whole list."
    },
    {
      "type": "paragraph",
      "text": "It's also worth remembering that these rankings aren't fixed. Lakes can shrink dramatically when the rivers feeding them are diverted — the Aral Sea in Central Asia was once among the largest lakes on Earth and has lost most of its water since the 1960s. Geography is a moving target, and satellite imagery is how we watch it move."
    }
  ]
};
